/**
 * **listto** ads — domain layer (the ONLY app-specific part).
 *
 * The rest of the kit (`types`/`gate`/`caps`/`config`/`provider`/`placements`/
 * `rewards`) is generic and copied as-is to the clones; this file is what
 * each app rewrites with its row in the ads strategy's "plan per app" table.
 *
 * listto row (tier **A**, RW+N+I+B):
 *   - breakpoints: completed list, closed day plan, weekly stats;
 *   - rewarded: +1 streak freeze, premium theme/font for 24 h, +24 h
 *     attachment quota.
 *
 * Rewards **reuse value that already exists** (gamification-core, the theme
 * catalog, the font registry, and plan quotas) instead of inventing a new
 * economy.
 */

import { definePlacements } from "./placements";
import type { AdRewardsStore } from "./rewards";
import type { AdTier } from "./types";

/** listto ad tier (§9): consumption, all formats. */
export const LISTTO_AD_TIER: AdTier = "A";

/** listto reward types (stable IDs: stored locally). */
export const REWARD_STREAK_FREEZE = "streak-freeze";
export const REWARD_THEME_UNLOCK = "theme-unlock";
export const REWARD_FONT_UNLOCK = "font-unlock";
export const REWARD_ATTACHMENT_QUOTA = "attachment-quota";

/** Extra attachment-quota units granted by a redemption (temporary, 24 h). */
export const ATTACHMENT_QUOTA_BONUS = 10;

/**
 * listto placement registry. It is the source of truth: a breakpoint not
 * listed here cannot render an ad, and the test guard checks that all are
 * legal for tier A.
 */
export const LISTTO_AD_PLACEMENTS = definePlacements({
  /** Completed list: the "done" screen is the canonical breather. */
  "list-done": { kind: "interstitial", breakpoint: "done-screen" },
  /** Closed day plan (all of today's tasks marked). */
  "day-plan-done": { kind: "interstitial", breakpoint: "done-screen" },
  /** Weekly stats: passive consumption ⇒ integrated native card. */
  "weekly-stats": { kind: "native", breakpoint: "stats" },
  /** Discrete banner anchored to the stats screen (passive). */
  "weekly-stats-banner": { kind: "banner", breakpoint: "stats" },
  /** Streak moment: "save your streak" — the Duolingo case. */
  "streak-save": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: { type: REWARD_STREAK_FREEZE, amount: 1 },
  },
  /** Try a premium theme/font for 24 h ("keep it with premium"). */
  "theme-trial": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: { type: REWARD_THEME_UNLOCK, amount: 1, durationHours: 24 },
  },
  "font-trial": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: { type: REWARD_FONT_UNLOCK, amount: 1, durationHours: 24 },
  },
  /** Raises the attachment quota for 24 h (the limit returns on its own). */
  "attachment-boost": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: { type: REWARD_ATTACHMENT_QUOTA, amount: ATTACHMENT_QUOTA_BONUS, durationHours: 24 },
  },
} as const);

export type ListtoPlacementId = keyof typeof LISTTO_AD_PLACEMENTS;

/**
 * Wiring with `gamification-core`: streak freezes earned through rewarded ads
 * are the `freezes` consumed by `computeStreak`. They are read from the rewards
 * store (local-first, works for guests) and added to those the player already
 * had.
 */
export function adFreezes(store: AdRewardsStore, now: number = Date.now()): number {
  return store.amountOf(REWARD_STREAK_FREEZE, now);
}

/** Is there an active temporary premium theme/font unlock? */
export function hasCosmeticUnlock(store: AdRewardsStore, now: number = Date.now()): boolean {
  return store.hasUnlock(REWARD_THEME_UNLOCK, now) || store.hasUnlock(REWARD_FONT_UNLOCK, now);
}

/**
 * Wiring with the monetization kit: a TEMPORARY bonus on the tier's attachment
 * limit. `limit === null` (unlimited) is respected as-is—adding a bonus to
 * "no limit" means nothing.
 */
export function attachmentLimitWithAdBonus(
  limit: number | null,
  store: AdRewardsStore,
  now: number = Date.now(),
): number | null {
  if (limit === null) {
    return null;
  }
  return limit + store.amountOf(REWARD_ATTACHMENT_QUOTA, now);
}
